'use client';

import React, { useState } from 'react';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogTitle } from '../../ui/dialog';
import Dropdown from '../../common/dropdown';

type OAuthModalValues = { role: string; company: string };

interface OAuthModalProps {
  onClose?: () => void;
  onSubmit?: (data: OAuthModalValues) => void;
  isOpen?: boolean;
  /** Why the modal reopened, e.g. a retryable registration failure. */
  notice?: string;
  /** What the user already entered, so a retry doesn't make them type it again. */
  initialValues?: OAuthModalValues;
}

const ROLE_OPTIONS = [
  { value: 'developer', label: 'Developer' },
  { value: 'contentManager', label: 'Content Manager' },
  { value: 'leader', label: 'Leader' },
];

export function OAuthModal({
  onClose,
  onSubmit,
  isOpen = false,
  notice,
  initialValues,
}: OAuthModalProps) {
  const [company, setCompany] = useState(initialValues?.company ?? '');
  const [role, setRole] = useState(initialValues?.role ?? '');
  const [errors, setErrors] = useState<{ company?: string; role?: string }>({});

  const clearFieldError = (field: 'company' | 'role') => {
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = () => {
    const newErrors: { company?: string; role?: string } = {};

    if (!company) {
      newErrors.company = 'Company is required';
    }

    if (!role) {
      newErrors.role = 'Role is required';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      onSubmit?.({ role, company });
      onClose?.();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose?.()}>
      <DialogContent className="login-modal-container p-8">
        <div className="login-modal-content">
          <DialogTitle className="h1-compact mb-6">Please provide more details</DialogTitle>

          {notice && (
            <div className="mb-4">
              <p className="cs-error">{notice}</p>
            </div>
          )}

          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <label htmlFor="company" className="cs-input-field flex items-center justify-between">
                Company
                <span className="cs-asterisk">*</span>
              </label>
              <Input
                id="company"
                type="text"
                placeholder="Company"
                value={company}
                onChange={(e) => {
                  setCompany(e.target.value);
                  clearFieldError('company');
                }}
                className={`bg-surface-5 cs-input-field hover:bg-surface-4 cursor-pointer touch-manipulation ${
                  errors.company ? 'border-destructive cs-input-ring-error' : ''
                }`}
              />
              {errors.company && (
                <div className="my-1">
                  <span className="cs-error">{errors.company}</span>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="role" className="cs-input-field flex items-center justify-between">
                Role
                <span className="cs-asterisk">*</span>
              </label>
              <Dropdown
                options={ROLE_OPTIONS}
                defaultValue={role}
                placeholder="Enter your role"
                onChange={(value) => {
                  setRole(value as string);
                  clearFieldError('role');
                }}
                className={`bg-surface-5 cs-input-field hover:bg-surface-4 cursor-pointer touch-manipulation ${
                  errors.role ? 'border-destructive' : ''
                }`}
              />
              {errors.role && (
                <div className="my-1">
                  <span className="cs-error">{errors.role}</span>
                </div>
              )}
            </div>

            <Button
              onClick={handleSubmit}
              variant="PrimaryFillButton"
              className="mt-2 flex !h-auto w-full touch-manipulation items-center justify-center gap-3 px-3 py-4 font-normal"
            >
              Submit
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
